/**
 * RESULTADOS PARA O PROFESSOR — as respostas de um aluno num código que ele copia e
 * envia: `R1.` seguido do JSON compacto em base64url.
 *
 * Por que código e não arquivo: numa página embutida (um Artifact, um ambiente
 * escolar fechado), baixar arquivo pode estar bloqueado — copiar texto, nunca. O
 * professor cola os códigos de todos na aba Relatório, de uma vez.
 *
 * O código leva, por resposta: item, seed, acerto, pontuação, erro nomeado, dicas e
 * hora. Não leva a resposta em si — o relatório é sobre erros, não sobre conferir
 * resposta por resposta.
 */

import { deBase64url, paraBase64url } from '../core/base64url.ts';
import { hasTemplate } from '../engine/registry.ts';
import type { Tentativa } from './historico.ts';

const PREFIXO = 'R1.';

export interface PacoteResultados {
  readonly nome: string;
  readonly tentativas: readonly Tentativa[];
}

export function codigoDeResultados(nome: string, ts: readonly Tentativa[]): string {
  const t = ts.map((x) => [
    x.templateId,
    x.seed,
    x.correto ? 1 : 0,
    Math.round(x.score * 100) / 100,
    x.erro ?? 0,
    x.dicas,
    x.t,
  ]);
  return PREFIXO + paraBase64url(JSON.stringify({ n: nome.trim().slice(0, 60), t }));
}

export function resultadosDoCodigo(texto: string): PacoteResultados | null {
  const s = texto.trim();
  if (!s.startsWith(PREFIXO)) return null;
  try {
    const o = JSON.parse(deBase64url(s.slice(PREFIXO.length))) as {
      n?: unknown;
      t?: unknown;
    } | null;
    if (!o || !Array.isArray(o.t)) return null;
    const tentativas: Tentativa[] = [];
    for (const x of o.t as unknown[]) {
      if (!Array.isArray(x) || typeof x[0] !== 'string' || !hasTemplate(x[0])) continue;
      const certo = x[2] === 1;
      tentativas.push({
        templateId: x[0],
        seed: String(x[1] ?? ''),
        correto: certo,
        score: typeof x[3] === 'number' ? x[3] : certo ? 1 : 0,
        erro: typeof x[4] === 'string' ? x[4] : null,
        dicas: typeof x[5] === 'number' ? x[5] : 0,
        t: typeof x[6] === 'number' ? x[6] : 0,
      });
    }
    const nome = typeof o.n === 'string' && o.n.trim() !== '' ? o.n.trim() : 'Sem nome';
    return { nome, tentativas };
  } catch {
    return null;
  }
}

/**
 * Vários códigos colados de uma vez — separados por espaço, vírgula ou quebra de linha.
 *
 * O MESMO CÓDIGO DUAS VEZES conta uma vez só. Ele é o mesmo aluno com as mesmas
 * respostas, até no horário de cada uma; e quem junta códigos de uma conversa de
 * turma cola a mensagem reenviada junto com a original. Contado duas vezes, ele virava
 * "Ana" e "Ana (2)" e dobrava o peso daquele aluno no relatório e na calibração.
 */
export function lerCodigos(texto: string): {
  readonly pacotes: PacoteResultados[];
  readonly invalidos: number;
  readonly repetidos: number;
} {
  const pacotes: PacoteResultados[] = [];
  const vistos = new Set<string>();
  let invalidos = 0;
  let repetidos = 0;
  for (const parte of texto.split(/[\s,;]+/).filter((p) => p.length > 0)) {
    if (vistos.has(parte)) {
      repetidos++;
      continue;
    }
    const r = resultadosDoCodigo(parte);
    if (!r) {
      invalidos++;
      continue;
    }
    vistos.add(parte);
    pacotes.push(r);
  }
  return { pacotes, invalidos, repetidos };
}
